import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api-client";
import type { components } from "../../lib/api-types.gen";
import { downloadFile } from "../../lib/download";
import { useElevation } from "../access/elevation";

/* Contract-derived types (ADR-S1-005). Hand-authored DTOs are forbidden; every
 * shape below is re-exported from the generated openapi-typescript output. */
export type DsgvoOverviewResponse = components["schemas"]["DsgvoOverviewResponse"];
export type DsgvoPerson = components["schemas"]["DsgvoPerson"];
export type DsgvoPersonOccurrences = components["schemas"]["DsgvoPersonOccurrences"];
export type DsgvoOccurrenceGroup = components["schemas"]["DsgvoOccurrenceGroup"];
export type DsgvoOccurrence = components["schemas"]["DsgvoOccurrence"];
export type DsgvoDataObject = components["schemas"]["DsgvoDataObject"];
export type DsgvoOccurrenceField = components["schemas"]["DsgvoOccurrenceField"];
export type DeletionJournalEntry = components["schemas"]["DeletionJournalEntry"];
export type DeletionJournalStatus = components["schemas"]["DeletionJournalStatus"];
export type DeletionJournalKind = components["schemas"]["DeletionJournalKind"];
export type DeletionDisposition = components["schemas"]["DeletionDisposition"];
export type DeletionExecutionResponse = components["schemas"]["DeletionExecutionResponse"];
export type CreateDeletionRequest = components["schemas"]["CreateDeletionRequest"];
export type ResolveManualReviewRequest = components["schemas"]["ResolveManualReviewRequest"];
export type DsgvoReviewItem = components["schemas"]["DsgvoReviewItem"];
export type DsgvoReviewItemStatus = components["schemas"]["DsgvoReviewItemStatus"];
export type EditReviewItemRequest = components["schemas"]["EditReviewItemRequest"];

export const dsgvoKeys = {
  all: ["dsgvo"] as const,
  overview: ["dsgvo", "me"] as const,
  people: (query: string) => ["dsgvo", "people", query] as const,
  occurrences: (userId: string) => ["dsgvo", "occurrences", userId] as const,
  journal: ["dsgvo", "journal"] as const,
  reviewItems: (journalId: string) => ["dsgvo", "review-items", journalId] as const,
};

/** GET /dsgvo/me - the caller's own data-category overview (Tab "Meine Daten"). */
export function useDsgvoOverview() {
  return useQuery({
    queryKey: dsgvoKeys.overview,
    queryFn: () => apiFetch<DsgvoOverviewResponse>("/dsgvo/me"),
  });
}

/**
 * GET /dsgvo/me/export - the person's own Art.-15 Datenauskunft as a JSON
 * download. The server names the file via Content-Disposition; the fallback
 * only covers an unparseable header.
 */
export function downloadDsgvoExport(): Promise<void> {
  return downloadFile("/dsgvo/me/export", "datenauskunft.json");
}

/** GET /dsgvo/people?query= - [F-024] function 1: people by any stored name or address. */
export function usePeopleSearch(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: dsgvoKeys.people(trimmed),
    queryFn: () => apiFetch<DsgvoPerson[]>(`/dsgvo/people?query=${encodeURIComponent(trimmed)}`),
    enabled: trimmed.length > 0,
  });
}

/** GET /dsgvo/people/{userId}/occurrences - the person's occurrences, grouped by data object. */
export function usePersonOccurrences(userId: string | null) {
  return useQuery({
    queryKey: dsgvoKeys.occurrences(userId ?? ""),
    queryFn: () => apiFetch<DsgvoPersonOccurrences>(`/dsgvo/people/${userId ?? ""}/occurrences`),
    enabled: userId !== null,
  });
}

/**
 * POST /dsgvo/people/{userId}/disclosure - [F-024] function 2, the Auskunft
 * im Auftrag as a download. A POST, because creating it writes the
 * Loeschjournal and the audit log.
 */
export function downloadDisclosure(userId: string): Promise<void> {
  return downloadFile(`/dsgvo/people/${userId}/disclosure`, "auskunft.json", { method: "POST" });
}

/** GET /dsgvo/deletion-journal - every erasure and every Auskunft, newest first. */
export function useDeletionJournal() {
  return useQuery({
    queryKey: dsgvoKeys.journal,
    queryFn: () => apiFetch<DeletionJournalEntry[]>("/dsgvo/deletion-journal"),
  });
}

/** GET /dsgvo/deletion-journal/{id}/review-items - an erasure's manual review. */
export function useReviewItems(journalId: string | null) {
  return useQuery({
    queryKey: dsgvoKeys.reviewItems(journalId ?? ""),
    queryFn: () => apiFetch<DsgvoReviewItem[]>(`/dsgvo/deletion-journal/${journalId ?? ""}/review-items`),
    enabled: journalId !== null,
  });
}

/** Everything on the DSGVO path hangs together: invalidate the whole scope after a write. */
function invalidateDsgvo(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: dsgvoKeys.all });
}

/**
 * POST /dsgvo/deletion-requests - [F-024] function 3, the erasure order. The
 * ONE place the order is sent from: it needs an elevated session ([F-033]),
 * and the server answers 403 with `requires: "elevation"` without one - the
 * integration wraps exactly this call in the elevation flow.
 */
export function useCreateDeletionRequest() {
  const queryClient = useQueryClient();
  const { withElevation } = useElevation();
  return useMutation({
    // Without an elevated session the app-wide dialog asks for the code and
    // sends the order again ([F-033]); cancelling rejects with
    // ElevationCancelled, which the order dialog does not show as an error.
    mutationFn: (subjectUserId: string) =>
      withElevation(() =>
        apiFetch<DeletionExecutionResponse>("/dsgvo/deletion-requests", {
          method: "POST",
          body: JSON.stringify({
            subjectUserId,
            dispositionConfirmed: true,
          } satisfies CreateDeletionRequest),
        }),
      ),
    onSuccess: () => invalidateDsgvo(queryClient),
  });
}

/** POST /dsgvo/deletion-journal/{id}/replay - an erasure again after a restore (ADR-C-011). */
export function useReplayDeletion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (journalId: string) =>
      apiFetch<DeletionExecutionResponse>(`/dsgvo/deletion-journal/${journalId}/replay`, { method: "POST" }),
    onSuccess: () => invalidateDsgvo(queryClient),
  });
}

export interface EditReviewItemVars {
  itemId: string;
  text: string;
}

/** POST /dsgvo/review-items/{id}/edit - the auditor's text into the field. */
export function useEditReviewItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, text }: EditReviewItemVars) =>
      apiFetch<DsgvoReviewItem>(`/dsgvo/review-items/${itemId}/edit`, {
        method: "POST",
        body: JSON.stringify({ text } satisfies EditReviewItemRequest),
      }),
    onSuccess: () => invalidateDsgvo(queryClient),
  });
}

/** POST /dsgvo/review-items/{id}/confirm - the field stays as it is. */
export function useConfirmReviewItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      apiFetch<DsgvoReviewItem>(`/dsgvo/review-items/${itemId}/confirm`, { method: "POST" }),
    onSuccess: () => invalidateDsgvo(queryClient),
  });
}

export interface ResolveManualReviewVars {
  journalId: string;
  note: string;
}

/** POST /dsgvo/deletion-journal/{id}/manual-review - close the review with a mandatory note. */
export function useResolveManualReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ journalId, note }: ResolveManualReviewVars) =>
      apiFetch<DeletionJournalEntry>(`/dsgvo/deletion-journal/${journalId}/manual-review`, {
        method: "POST",
        body: JSON.stringify({ note } satisfies ResolveManualReviewRequest),
      }),
    onSuccess: () => invalidateDsgvo(queryClient),
  });
}
